import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
  useSyncExternalStore,
  type ComponentProps,
} from "react";
import { cn } from "@repo/blacksmith-ui/cn";
import { useKnownImageSize } from "./sessionInlineImages";

/**
 * A hung image request must not strand the session behind the loading
 * screen; the browser's own timeout is far longer than a user will wait.
 */
const MAX_HOLD_MS = 10_000;

const noop = () => undefined;

export type TranscriptHoldKind = "chunk" | "image" | "highlight" | "other";

const HOLD_KINDS: readonly TranscriptHoldKind[] = [
  "chunk",
  "image",
  "highlight",
  "other",
];

export interface TranscriptLoadGateStats {
  /** `ms` is the union of that kind's hold intervals, not their sum. */
  holds: Record<TranscriptHoldKind, { count: number; ms: number }>;
  timedOut: boolean;
  lastReleaseKind: TranscriptHoldKind | null;
}

interface KindTally {
  count: number;
  active: number;
  since: number;
  ms: number;
}

/**
 * Holds a session behind its loading screen until the first-rendered
 * transcript has fully landed: lazily loaded chunks resolved and every image
 * without a reserved box loaded (or failed). Armed once the transcript
 * commits, so holds taken during that commit are counted; settles
 * permanently the first time nothing holds it, after which later images
 * (streamed turns) are ignored.
 */
export class TranscriptLoadGate {
  private holds = 0;
  private reservedLoading = 0;
  private armed = false;
  private settled = false;
  private timedOut = false;
  private lastReleaseKind: TranscriptHoldKind | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private readonly listeners = new Set<() => void>();
  private readonly tallies = new Map<TranscriptHoldKind, KindTally>(
    HOLD_KINDS.map((kind) => [kind, { count: 0, active: 0, since: 0, ms: 0 }]),
  );

  hold(kind: TranscriptHoldKind = "other"): () => void {
    if (this.settled) return noop;
    const tally = this.tallies.get(kind)!;
    tally.count += 1;
    if (tally.active === 0) tally.since = performance.now();
    tally.active += 1;
    this.holds += 1;
    this.emit();
    let released = false;
    return () => {
      if (released) return;
      released = true;
      tally.active -= 1;
      if (tally.active === 0) tally.ms += performance.now() - tally.since;
      this.lastReleaseKind = kind;
      this.holds -= 1;
      this.emit();
    };
  }

  /**
   * An image with both width and height attributes has its exact box before
   * its bytes arrive, so it cannot move the transcript and never holds; it
   * only delays `imagesLoaded`.
   */
  holdImage(img: HTMLImageElement): () => void {
    if (this.settled || img.complete) return noop;
    const reserved = img.hasAttribute("width") && img.hasAttribute("height");
    const release = reserved ? this.trackReserved() : this.hold("image");
    const done = () => {
      img.removeEventListener("load", done);
      img.removeEventListener("error", done);
      release();
    };
    img.addEventListener("load", done);
    img.addEventListener("error", done);
    return done;
  }

  arm(): void {
    if (this.armed) return;
    this.armed = true;
    this.timer = setTimeout(() => {
      this.timedOut = !this.settled;
      this.settle();
    }, MAX_HOLD_MS);
    this.emit();
  }

  get isSettled(): boolean {
    return this.settled;
  }

  get isReady(): boolean {
    return this.settled || (this.armed && this.holds === 0);
  }

  /** Ready, and every reserved image in the first-rendered transcript has loaded (or failed) too. */
  get imagesLoaded(): boolean {
    return this.isReady && this.reservedLoading === 0;
  }

  settle(): void {
    if (this.settled) return;
    this.settled = true;
    this.clearTimer();
    this.emit();
  }

  stats(): TranscriptLoadGateStats {
    const now = performance.now();
    const holds = {} as TranscriptLoadGateStats["holds"];
    for (const [kind, tally] of this.tallies) {
      const open = tally.active > 0 ? now - tally.since : 0;
      holds[kind] = { count: tally.count, ms: Math.round(tally.ms + open) };
    }
    return {
      holds,
      timedOut: this.timedOut,
      lastReleaseKind: this.lastReleaseKind,
    };
  }

  dispose(): void {
    this.clearTimer();
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  private trackReserved(): () => void {
    this.reservedLoading += 1;
    let released = false;
    const release = () => {
      if (released) return;
      released = true;
      clearTimeout(timer);
      this.reservedLoading -= 1;
      this.emit();
    };
    const timer = setTimeout(release, MAX_HOLD_MS);
    return release;
  }

  private clearTimer(): void {
    if (this.timer === null) return;
    clearTimeout(this.timer);
    this.timer = null;
  }

  private emit(): void {
    for (const listener of this.listeners) listener();
  }
}

export const TranscriptLoadGateContext =
  createContext<TranscriptLoadGate | null>(null);

/**
 * One gate per thread. `active` is whether the transcript is rendered
 * (rather than the pane's own loading shell); the gate arms in a layout
 * effect, which runs after the transcript's refs and layout effects have
 * registered their holds. `skip` settles it outright for a transcript with
 * nothing to fetch (an optimistic first send, whose attachments are inline
 * data and whose handoff animation must not be covered).
 */
export function useTranscriptLoadGate(
  threadKey: string | null,
  { active, skip = false }: { active: boolean; skip?: boolean },
): { gate: TranscriptLoadGate; ready: boolean; imagesLoaded: boolean } {
  const [current, setCurrent] = useState(() => ({
    key: threadKey,
    gate: new TranscriptLoadGate(),
  }));
  let gate = current.gate;
  if (current.key !== threadKey) {
    gate = new TranscriptLoadGate();
    setCurrent({ key: threadKey, gate });
  }
  useEffect(() => () => gate.dispose(), [gate]);

  const ready = useSyncExternalStore(
    gate.subscribe,
    () => gate.isReady,
    () => true,
  );
  const imagesLoaded = useSyncExternalStore(
    gate.subscribe,
    () => gate.imagesLoaded,
    () => true,
  );
  useLayoutEffect(() => {
    if (skip) gate.settle();
    else if (active) gate.arm();
  }, [active, gate, skip]);
  useLayoutEffect(() => {
    if (ready) gate.settle();
  }, [gate, ready]);

  return { gate, ready, imagesLoaded };
}

const subscribeNever = () => noop;

/** Whether the enclosing thread's first-rendered transcript has landed; true without a gate. */
export function useTranscriptLanded(): boolean {
  const gate = useContext(TranscriptLoadGateContext);
  return useSyncExternalStore(
    gate?.subscribe ?? subscribeNever,
    () => gate?.isReady ?? true,
    () => true,
  );
}

/**
 * `<img>` for transcript content: holds the gate until it loads unless its
 * box is reserved (see holdImage). Mounted while the gate is open, it loads
 * eagerly: a lazy image outside the viewport never fires `load`, and the
 * pinned-to-bottom transcript puts most of a long thread there. A reserved
 * image loads eagerly too, so the one on screen at reveal is not left empty
 * until the browser's lazy loading reaches it after the first paint.
 */
export function TranscriptImage(props: ComponentProps<"img">) {
  const gate = useContext(TranscriptLoadGateContext);
  const [loading] = useState<"eager" | "lazy">(() =>
    gate && !gate.isSettled ? "eager" : "lazy",
  );
  const ref = useCallback(
    (img: HTMLImageElement | null) => {
      if (!img || !gate) return undefined;
      return gate.holdImage(img);
    },
    [gate],
  );
  // eslint-disable-next-line @next/next/no-img-element
  return <img loading={loading} {...props} ref={ref} />;
}

/**
 * TranscriptImage for a thumbnail capped at `maxHeight` (and `maxWidth`) px.
 * A server-known size reserves the box the loaded image would take, e.g.
 * 1440x900 under a 192px cap → 307.2px wide at 1440 / 900, so it skips the gate.
 * The explicit (non-`auto`) aspect-ratio keeps the box from following the
 * natural ratio once the bytes arrive; the width/height attributes stay the
 * integer server size, which is what holdImage reads as a known box.
 */
export function TranscriptThumbnail({
  maxHeight,
  maxWidth = Infinity,
  className,
  style,
  ...props
}: ComponentProps<"img"> & {
  src: string;
  maxHeight: number;
  maxWidth?: number;
}) {
  const size = useKnownImageSize(props.src);
  if (!size)
    return <TranscriptImage {...props} className={className} style={style} />;
  // Multiply before dividing: 1440 * (192 / 900) is 307.20000000000005.
  const width = Math.min(
    size.width,
    (size.width * maxHeight) / size.height,
    maxWidth,
  );
  return (
    <TranscriptImage
      {...props}
      width={size.width}
      height={size.height}
      className={cn(className, "h-auto")}
      style={{
        ...style,
        width,
        aspectRatio: `${size.width} / ${size.height}`,
      }}
    />
  );
}

/** `loading` for `next/dynamic` transcript chunks: holds the gate until the chunk renders. */
export function TranscriptChunkFallback() {
  const gate = useContext(TranscriptLoadGateContext);
  useLayoutEffect(() => gate?.hold("chunk"), [gate]);
  return null;
}
